import { useMutation, useQuery } from "@apollo/client/react";
import { Grid } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import VisibilityIcon from "@mui/icons-material/Visibility";
import { CustomButton } from '../../../../components/customButton/CustomButton';
import toast from 'react-hot-toast';
import DocumentViewer from '../../../../components/DocumentViewer';

import { RenderInputByTypeMobile } from '../../../../components/formGroupGenerator/RenderInputByTypeMobile';
import GET_CONSENTIMIENTO_PACIENTE from '../../../../data/graphQL/queries/GestionAsistencial/getConsentimientoPaciente';
import { obtenerDataImagen } from '../../../../actions/ImagenAction';
import moment from 'moment';
import { getParsedConsentimientoInput, SAVE_CONSENTIMIENTO_PACIENTE } from '../../../../data/graphQL/mutations/GestionAsistencial/updateConsentimientoAsistencial';

const CONSENTIMIENTO_SI = 'SI';
const CONSENTIMIENTO_NA = 'N/A';

export const TabDocumentacionLegal = ({
  allowedWrite,
  evaluacionSelected,
  idPaciente,
  getContratos
}) => {
  const [viewDocumentData, setViewDocumentData] = useState({});
  const [documentacionLegalData, setDocumentacionLegalData] = useState({
    consentimiento: evaluacionSelected?.consentimiento
  });

  const { data: consentimientoData } = useQuery(GET_CONSENTIMIENTO_PACIENTE, {
    fetchPolicy: 'no-cache',
    variables: {
      idPaciente,
      idTipoModulo: evaluacionSelected.idTipoModulo
    },
    skip: !evaluacionSelected?.idTipoModulo || !idPaciente
  });

  const [guardar] = useMutation(SAVE_CONSENTIMIENTO_PACIENTE, {
    refetchQueries: [GET_CONSENTIMIENTO_PACIENTE],
    variables: {
      consentimientoInput: getParsedConsentimientoInput(documentacionLegalData, idPaciente, evaluacionSelected.idTipoModulo)
    }
  });

  const valorConsentimiento = useMemo(() =>
    documentacionLegalData?.consentimiento === CONSENTIMIENTO_SI,
    [documentacionLegalData?.consentimiento]);

  const valorAdjunto = useMemo(() => {
    if (!documentacionLegalData?.archivo) return null;

    const { archivo, titulo, contentType, extension } = documentacionLegalData;

    return {
      archivo, titulo, contentType, extension
    };
  }, [documentacionLegalData?.archivo]);

  const updatedFields = useMemo(() => {
    return structuredClone(fields).
      map((input) => {
        const updatedInput = { ...input };
        switch (input.name) {
          case 'consentimientoAdjunto':
            updatedInput.disabled = documentacionLegalData?.consentimiento === CONSENTIMIENTO_NA;
            break;
          case 'fechaFirma':
            updatedInput.disabled = !!documentacionLegalData?.idArchivo || documentacionLegalData?.consentimiento === CONSENTIMIENTO_NA;
            break;
          default:
            break;
        }
        if (!allowedWrite) {
          updatedInput.disabled = true;
        }
        return updatedInput;
      });
  }, [documentacionLegalData?.idArchivo, documentacionLegalData?.consentimiento]);

  useEffect(() => {
    setDocumentacionLegalData({
      ...consentimientoData?.consentimientoPaciente,
      consentimiento: evaluacionSelected?.consentimiento
    });
  }, [consentimientoData]);

  useEffect(() => {
    setDocumentacionLegalData((prev) => ({
      ...prev,
      consentimiento: evaluacionSelected?.consentimiento
    }));
  }, [
    evaluacionSelected?.consentimiento]
  );

  const isValid = () => {
    const errorMessages = [];

    const { fechaFirma } = documentacionLegalData;

    if (!fechaFirma || !moment(fechaFirma).isValid()) {
      errorMessages.push('Debe ingresar una Fecha de Firma válida.\n');
    }

    if (moment(fechaFirma).isAfter(moment())) {
      errorMessages.push('La Fecha de Firma no puede ser superior a la fecha actual.\n');
    }

    if (evaluacionSelected?.fechaHasta && moment(fechaFirma).isAfter(moment(evaluacionSelected.fechaHasta))) {
      errorMessages.push('La Fecha de Firma no puede ser posterior a la fecha de fin de la evaluación.\n');
    }

    if (errorMessages.length > 0) {
      toast.error(errorMessages.join(' '));
      return false;
    }

    return true;
  };

  const handleClickViewDocument = () => {
    setViewDocumentData({ ...documentacionLegalData });
  };

  const reserViewDocument = () => {
    setViewDocumentData({});
  };

  const handleSubmit = () => {
    if (isValid()) {
      toast.promise(
        guardar({
          onCompleted: () => {
            getContratos();
          }
        }), {
        loading: 'Guardando "Documentación Legal"...',
        success: '"Documentación Legal" guardada.',
        error: 'Ocurrió un error al intentar guardar "Documentación Legal".'
      }
      );
    }
  };

  const handleChange = (name, value) => {
    if (name !== 'consentimientoAdjunto') {
      setDocumentacionLegalData((prev) => ({
        ...prev,
        [name]: value
      }));
    } else {
      obtenerDataImagen(value).then((image) => {
        const fechaActual = moment().format("yyyy-MM-DD");
        setDocumentacionLegalData((prev) => ({
          ...prev,
          contentType: image?.tipo,
          archivo: image?.data,
          extension: image?.extension,
          titulo: image?.nombre,
          fechaAlta: fechaActual
        })
        );

      });
    }
  };

  const handleDelete = async (name) => {
    setDocumentacionLegalData((prev) => ({
      ...prev,
      contentType: null,
      archivo: null,
      extension: null,
      titulo: null,
      fechaAlta: null
    }));
  };

  return (
    <div className='generic-tab-container'>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <div className='button-save-geo-data'>
            {allowedWrite && documentacionLegalData?.consentimiento !== CONSENTIMIENTO_NA && <CustomButton
              onClick={handleSubmit}
              label='Guardar'
              variant='outlined' />
            }
          </div>
        </Grid>
        {updatedFields.
          map((input, index) => {
            return (
              <React.Fragment key={`input-${input.name}-index-${index}`}>
                {viewDocumentData.idArchivo && <DocumentViewer data={viewDocumentData} setOpenDlg={() => reserViewDocument()} />}
                <Grid key={`input-${input.name}-index-${index}`} size={{ md: input.col, xs: input.type !== 'item' ? 12 : 0 }}>
                  {input.name === 'viewDocument' ?
                    documentacionLegalData.idArchivo ?
                      <VisibilityIcon className="edit-icon" onClick={() => handleClickViewDocument()} /> :
                      null :
                    <RenderInputByTypeMobile
                      key={`input-${input.name}-index-${index}`}
                      input={input}
                      handleChange={handleChange}
                      handleDelete={input.name === 'consentimientoAdjunto' ? handleDelete : null}
                      value={input.name === 'consentimiento' ? valorConsentimiento :
                        input.name === 'consentimientoAdjunto' ? valorAdjunto :
                          documentacionLegalData[input.name]
                      } />
                  }
                </Grid>
              </React.Fragment>);

          })
        }
      </Grid>
    </div>);

};


const fields = [
  //row
  {
    displayName: 'Consentimiento informado',
    name: 'consentimiento',
    type: 'custom_switch',
    disabled: true,
    col: 3.5
  },
  {
    displayName: 'Adjuntar CI',
    name: 'consentimientoAdjunto',
    type: 'file',
    isDesktop: true,
    col: 5
  },
  {
    displayName: 'Fecha de Firma CI',
    name: 'fechaFirma',
    type: 'date',
    disabled: true,
    col: 3
  }];